import { createImageEngine } from '../image/create-image-engine.js';
import { SearchController } from '../search/search-controller.js';
import { PdfImageSource } from './pdf-source.js';
import { createPdfHighlightOverlay } from './pdf-highlights.js';
/**
 * A PDF reader: the image engine driving pdf.js-rendered pages. Reuses every
 * image behaviour (paged/continuous, zoom/pan, fit, gestures, preload) and adds
 * the document outline as `reader:toc`, plus Shift+drag rect highlights over
 * the page (single-page mode).
 */
export function createPdfEngine(options) {
    const pdfSource = new PdfImageSource(options.source, options.pdf);
    const engine = createImageEngine({ ...options, source: pdfSource });
    const tocListeners = new Set();
    const searchListeners = new Set();
    const selectionListeners = new Set();
    const highlightListeners = new Set();
    let toc = [];
    // ---- highlights ----------------------------------------------------------
    const { source, bookId, container } = options;
    let highlights = [];
    let page = 0;
    let saveTimer;
    const emitHighlights = () => {
        for (const l of highlightListeners)
            l({ highlights });
    };
    const scheduleSave = () => {
        if (!source.saveHighlights)
            return;
        clearTimeout(saveTimer);
        saveTimer = setTimeout(() => void source.saveHighlights?.(bookId, highlights).catch(() => { }), 400);
    };
    const overlay = createPdfHighlightOverlay({
        container,
        textRects: (p1) => pdfSource.textRects(p1),
        currentPage: () => page,
        onSelection: (sel) => {
            for (const l of selectionListeners)
                l(sel);
        },
    });
    const locOff = engine.on('reader:locationchange', (p) => {
        page = p.page;
        overlay.repaint();
    });
    const zoomOff = engine.on('reader:zoomchange', () => overlay.repaint());
    const layoutOff = engine.on('reader:layoutchange', () => overlay.repaint());
    const readyOff = engine.on('reader:ready', () => {
        // the image engine calls container.replaceChildren(root) during mount, so
        // the overlay can only be attached once that has happened
        overlay.attach();
        void pdfSource.outline().then((o) => {
            toc = o;
            for (const l of tocListeners)
                l({ toc });
        });
        void (async () => {
            highlights = ((await source.loadHighlights?.(bookId)) ?? []).filter((h) => h.kind === 'rect');
            overlay.setHighlights(highlights);
            emitHighlights();
        })();
    });
    const addHighlight = (opts) => {
        const pending = overlay.takePending();
        if (!pending)
            return null;
        const rec = {
            kind: 'rect',
            id: `hl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
            page: pending.page,
            rects: pending.rects,
            color: opts?.color ?? 'yellow',
            ...(opts?.note !== undefined ? { note: opts.note } : {}),
            text: pending.text,
            createdAt: Date.now(),
        };
        highlights = [...highlights, rec];
        overlay.setHighlights(highlights);
        overlay.clearPending();
        scheduleSave();
        emitHighlights();
        return rec;
    };
    const removeHighlight = (id) => {
        const next = highlights.filter((h) => h.id !== id);
        if (next.length === highlights.length)
            return;
        highlights = next;
        overlay.setHighlights(highlights);
        scheduleSave();
        emitHighlights();
    };
    const updateHighlight = (id, patch) => {
        const idx = highlights.findIndex((h) => h.id === id);
        if (idx === -1)
            return null;
        const next = {
            ...highlights[idx],
            ...(patch.color !== undefined ? { color: patch.color } : {}),
        };
        if (patch.note !== undefined) {
            if (patch.note === '')
                delete next.note;
            else
                next.note = patch.note;
        }
        highlights = highlights.map((h, i) => (i === idx ? next : h));
        overlay.setHighlights(highlights);
        scheduleSave();
        emitHighlights();
        return next;
    };
    // ---- search -------------------------------------------------------------
    const search = new SearchController(options.searchWorkerFactory !== undefined
        ? { workerFactory: options.searchWorkerFactory }
        : {});
    let searchBuilt = null;
    const buildSearch = async () => {
        const count = await pdfSource.pageCount();
        const sections = await Promise.all(Array.from({ length: count }, async (_, i) => ({
            id: `page:${i + 1}`,
            index: i,
            text: await pdfSource.textContent(i + 1),
        })));
        await search.build(sections);
    };
    const runSearch = async (query) => {
        searchBuilt ??= buildSearch();
        await searchBuilt;
        const hits = await search.query(query, { limit: 300 });
        for (const l of searchListeners)
            l({ query, hits });
        return hits;
    };
    const gotoHit = (hit) => engine.goto(hit.sectionIndex);
    return {
        ...engine,
        on(event, handler) {
            if (event === 'reader:toc') {
                const l = handler;
                tocListeners.add(l);
                if (toc.length)
                    l({ toc });
                return () => tocListeners.delete(l);
            }
            if (event === 'reader:searchresults') {
                const l = handler;
                searchListeners.add(l);
                return () => searchListeners.delete(l);
            }
            if (event === 'reader:selection') {
                const l = handler;
                selectionListeners.add(l);
                return () => selectionListeners.delete(l);
            }
            if (event === 'reader:highlightschange') {
                const l = handler;
                highlightListeners.add(l);
                l({ highlights });
                return () => highlightListeners.delete(l);
            }
            return engine.on(event, handler);
        },
        search: runSearch,
        gotoHit,
        addHighlight,
        removeHighlight,
        updateHighlight,
        listHighlights: () => highlights,
        destroy() {
            readyOff();
            locOff();
            zoomOff();
            layoutOff();
            clearTimeout(saveTimer);
            overlay.destroy();
            tocListeners.clear();
            searchListeners.clear();
            selectionListeners.clear();
            highlightListeners.clear();
            search.destroy();
            engine.destroy();
            void pdfSource.dispose();
        },
    };
}
//# sourceMappingURL=create-pdf-engine.js.map